/**
 * FarmersPulse Marketing Assistant — 12 tool preview blocks (Tools 13–24).
 *
 * Data shape identical to `farmerToolkit.js` so the same PhoneMock and
 * FarmerToolkitHub / FarmerToolPreview components render them without
 * changes. Every tool declares `category: "marketing"` so the hub can
 * split Farm Management vs Marketing with a tab filter.
 *
 * Language rules for every string in this file (mirrors the Marketing
 * Assistant spec at /app/memory/FARMERSPULSE_MARKETING_ASSISTANT_SPEC.md):
 *  - Plain-farmer language, no jargon.
 *  - No invented prices, quantities, dates, customer counts — every
 *    number is illustrative and framed as "example" (chip on preview).
 *  - Approval-first: every action-y CTA reads "Review draft" or
 *    "Approve & send", never "Post now".
 */

export const MARKETING_TOOLS = [
  {
    slug: "brand-builder",
    stage: 1,
    category: "marketing",
    name: "Farm Brand Builder",
    iconName: "shop",
    tagline: "Your farm story, ready to share.",
    elevator: "Turn your farm's name, story and photos into a small brand pack — profile page, tagline, About text and a printable QR poster you can hand to any customer.",
    what: [
      "Answer 4 short questions — farm name, place, what you grow, one photo.",
      "Get a tagline, About text and a homepage headline in your voice.",
      "One-tap QR poster ready to print for markets and farm stands.",
      "Consistent tone across email, social and your farm page.",
    ],
    how: [
      { title: "Tell us your farm story", body: "Just the basics — name, place, what you grow, why you started." },
      { title: "Pick a tone", body: "Folksy · straight-talking · playful — the assistant matches every future message to it." },
      { title: "Approve the brand pack", body: "Tagline, About text, QR poster — nothing goes live until you say yes." },
    ],
    status: "building",
    deepLink: "/websites",
    mocks: [
      {
        eyebrow: "Brand builder",
        title: "Tell us about your farm",
        subtitle: "Four short questions. About 3 minutes.",
        blocks: [
          { kind: "input",    label: "Farm name",    value: "Green Valley Farm", icon: "shop" },
          { kind: "input",    label: "Nearest town", value: "River City, OR",    icon: "map" },
          { kind: "textarea", label: "One line of your story", value: "Family farm for 30 years — no-till, no-spray, picked in the morning." },
          { kind: "chips",    label: "Farm voice", options: ["Folksy", "Straight-talking", "Playful"], active: "Folksy" },
          { kind: "cta",      label: "Draft my brand pack", icon: "sparkle" },
        ],
      },
      {
        eyebrow: "Brand pack draft",
        title: "Ready for your approval",
        subtitle: "Every piece is editable. Nothing goes live yet.",
        badge: "Approval required",
        blocks: [
          { kind: "textarea", label: "Tagline",   value: "Picked in the morning. On your table tonight." },
          { kind: "textarea", label: "About text (short)",
            value: "Green Valley Farm has fed River City for thirty years. Family-run, no-till, no-spray — the taste comes from the soil, not the sprayer." },
          { kind: "list", title: "Ready to publish", items: [
            { icon: "check", title: "Farm page hero", sub: "Headline + tagline" },
            { icon: "check", title: "Printable QR poster",  sub: "For the market table" },
            { icon: "check", title: "Social bio + About paragraph" },
          ]},
          { kind: "cta", label: "Approve & publish", icon: "check" },
        ],
      },
    ],
  },

  {
    slug: "product-marketing",
    stage: 1,
    category: "marketing",
    name: "Product Marketing Creator",
    iconName: "basket",
    tagline: "Every product, ready to sell.",
    elevator: "One photo and a price becomes a complete listing — description, selling points, storage tips and a share-ready caption. All in your farm's voice.",
    what: [
      "Snap a photo and the assistant writes the product page for you.",
      "Selling points highlight freshness, taste, growing method.",
      "Storage & use tips help customers get the best of what they bought.",
      "One-click share to social, WhatsApp, email — every channel worded right.",
    ],
    how: [
      { title: "Pick the product", body: "From your inventory — the assistant already knows the crop and price." },
      { title: "Add a photo", body: "One good shot is enough." },
      { title: "Approve or edit each channel", body: "Website, social, WhatsApp — approve any or all." },
    ],
    status: "building",
    deepLink: "/inventory",
    mocks: [
      {
        eyebrow: "New product",
        title: "Roma tomato — draft copy",
        subtitle: "One photo → website, social and WhatsApp — all in your voice.",
        blocks: [
          { kind: "gallery", n: 3 },
          { kind: "input", label: "Price",    value: "$2.35 / kg (from inventory)", icon: "coins" },
          { kind: "chips", label: "Selling points", options: ["Picked today", "No spray", "Firm & sweet", "Great for sauce"], active: "Picked today" },
          { kind: "cta",   label: "Draft the listing", icon: "sparkle" },
        ],
      },
      {
        eyebrow: "Multi-channel draft",
        title: "Roma tomatoes — approve to publish",
        subtitle: "Same product, worded right for each channel.",
        badge: "Approval required",
        blocks: [
          { kind: "textarea", label: "Website description",
            value: "Firm, sweet Roma tomatoes — picked this morning at Green Valley. No spray. Best on the counter today; ripe in the sun tomorrow." },
          { kind: "textarea", label: "Social caption",
            value: "Roma season is on. Picked at sunrise, on your table by dinner. Farm stand open till 6." },
          { kind: "textarea", label: "WhatsApp (short)",
            value: "Roma tomatoes ready — picked this morning. $2.35/kg. Reply to reserve." },
          { kind: "cta", label: "Approve all · publish", icon: "send" },
        ],
      },
    ],
  },

  {
    slug: "social-post-maker",
    stage: 1,
    category: "marketing",
    name: "AI Social Post Maker",
    iconName: "send",
    tagline: "A post that sells, in seconds.",
    elevator: "Tell the assistant one fact — 'Roma tomatoes peak this week' — and it drafts a caption, hashtag set and a photo direction. You approve before it goes anywhere.",
    what: [
      "One-line prompt → caption, hashtags, and a shot-list note for your photo.",
      "Post themes cover what's fresh, what's coming, a farm story, and a market reminder.",
      "Auto-matches your farm's tone from the Brand Builder.",
      "Never posts anything without your tap.",
    ],
    how: [
      { title: "Pick a theme", body: "Fresh today · new arrival · coming soon · a farm story." },
      { title: "Add one fact", body: "'Roma tomatoes hit peak flavor this week.'" },
      { title: "Approve the draft", body: "Caption + hashtags + a note on what to photograph." },
    ],
    status: "designed",
    deepLink: null,
    mocks: [
      {
        eyebrow: "New social post",
        title: "Compose a post",
        subtitle: "One fact in — a full caption out.",
        blocks: [
          { kind: "chips", label: "Theme", options: ["Fresh today", "Coming soon", "Farm story", "Market reminder"], active: "Fresh today" },
          { kind: "textarea", label: "One fact",
            value: "Roma tomatoes hit peak flavor this week. Curry leaves back after the rain break." },
          { kind: "chips", label: "Voice", options: ["Folksy", "Straight-talking", "Playful"], active: "Folksy" },
          { kind: "cta", label: "Draft this post", icon: "sparkle" },
        ],
      },
      {
        eyebrow: "Post draft",
        title: "Ready to review",
        badge: "Approval required",
        blocks: [
          { kind: "textarea", label: "Caption",
            value: "The Romas hit their sweet spot this week — dark red, firm, tasting like the whole summer at once. Curry leaves are back too. Farm stand open till 6." },
          { kind: "textarea", label: "Hashtags",
            value: "#EatLocal #RiverCityFarms #RomaTomato #FromTheFarm #GreenValleyFarm" },
          { kind: "info", text: "Photo direction: single Roma cluster in the vine — golden-hour light, close-up." },
          { kind: "cta",  label: "Approve · schedule to my page", icon: "send" },
        ],
      },
    ],
  },

  {
    slug: "email-whatsapp-campaigns",
    stage: 2,
    category: "marketing",
    name: "Email & WhatsApp Campaign Maker",
    iconName: "envelope",
    tagline: "Reach your regulars, plain and simple.",
    elevator: "The assistant writes your email subject + body and a short WhatsApp/SMS version — every message in your voice, and never sent without you tapping approve.",
    what: [
      "Pick who to reach in plain terms — 'past customers', 'market regulars', 'CSA members'.",
      "Get a full email plus a short WhatsApp/SMS version in one draft.",
      "Optional translated versions when your customers need them.",
      "Send-window suggestions — 'Thursday 5 PM works best for your list.'",
    ],
    how: [
      { title: "Pick the audience", body: "In everyday words — 'past berry buyers', 'CSA members', 'nearby'." },
      { title: "Say what you want to tell them", body: "'Strawberries peak this weekend — reserve a flat.'" },
      { title: "Approve both drafts", body: "Email + WhatsApp — approve any or both." },
    ],
    status: "designed",
    deepLink: null,
    mocks: [
      {
        eyebrow: "New campaign",
        title: "Compose",
        blocks: [
          { kind: "chips", label: "Who receives it", options: ["Past customers", "Market regulars", "CSA members", "Nearby"], active: "Past customers" },
          { kind: "textarea", label: "What to tell them",
            value: "Strawberries peak this weekend. Farm stand open Sat–Sun. Reserve a flat by Friday." },
          { kind: "chips", label: "Length", options: ["Short", "Medium"], active: "Short" },
          { kind: "cta", label: "Draft email + WhatsApp", icon: "sparkle" },
        ],
      },
      {
        eyebrow: "Draft for review",
        title: "Two versions, one approval",
        badge: "Approval required",
        blocks: [
          { kind: "input", label: "Subject line", value: "Strawberries peak this weekend at Green Valley", icon: "envelope" },
          { kind: "textarea", label: "Email body",
            value: "Hi — the strawberries hit their sweet spot this weekend. The farm stand is open Saturday and Sunday, 8 to 6. Reserve a flat by Friday and we'll set it aside with your name on it." },
          { kind: "textarea", label: "WhatsApp version",
            value: "Strawberries peak this weekend. Farm stand Sat–Sun 8–6. Reply to reserve a flat." },
          { kind: "info", text: "Best send window based on your past opens: Thursday 5 PM.", tone: "ok" },
          { kind: "cta", label: "Approve · queue to send", icon: "send" },
        ],
      },
    ],
  },

  {
    slug: "deals-promotions",
    stage: 2,
    category: "marketing",
    name: "Deals & Promotion Builder",
    iconName: "coins",
    tagline: "Move it before it wilts.",
    elevator: "Give the assistant a product, a quantity and a goal. It returns three offer shapes — discount, bundle, or flash sale — priced so you still make money.",
    what: [
      "Three offer shapes tailored to your goal: sell fast, clear surplus, or reward regulars.",
      "Break-even guardrail — the assistant never suggests a price that loses you money.",
      "One-tap fan-out to social, WhatsApp, farm-page banner and QR flyer.",
      "Surplus mode links straight to the FoodPulse donation flow if nothing sells.",
    ],
    how: [
      { title: "Pick a product and quantity", body: "'40 kg tomatoes. Need to move by Sunday.'" },
      { title: "Pick a goal", body: "Move fast · reward regulars · fill a slow day." },
      { title: "Choose an offer", body: "Three options — pick one, approve the copy, it goes live." },
    ],
    status: "designed",
    deepLink: null,
    mocks: [
      {
        eyebrow: "Move surplus fast",
        title: "40 kg Roma — three offer shapes",
        blocks: [
          { kind: "stat-row", items: [
            { label: "On hand", value: "40 kg" },
            { label: "Break-even", value: "$1.11 / kg" },
          ]},
          { kind: "list", items: [
            { icon: "coins", title: "10% off the flat", sub: "Simple discount · easy to communicate", right: "Pick" },
            { icon: "coins", title: "Buy 2 kg get 1 kg free", sub: "Bundle · lifts basket size",       right: "Pick" },
            { icon: "coins", title: "Flash 24h · $1.95/kg",  sub: "Fastest sell-through",             right: "Pick" },
          ]},
          { kind: "info", text: "Every offer stays above your break-even.", tone: "ok" },
        ],
      },
      {
        eyebrow: "Flash offer draft",
        title: "24-hour Roma flash — ready to publish",
        badge: "Approval required",
        blocks: [
          { kind: "textarea", label: "Farm-page banner", value: "24-hour flash: $1.95/kg Roma tomatoes — until 6 PM Sunday." },
          { kind: "textarea", label: "WhatsApp blast",   value: "24-hour flash: Roma tomatoes $1.95/kg. Ends Sun 6 PM. Reply to reserve." },
          { kind: "textarea", label: "QR flyer",         value: "Scan → today's flash offer, updated live." },
          { kind: "info", text: "If more than 15 kg remains Monday, the assistant will suggest a donation route.", tone: "ok" },
          { kind: "cta", label: "Approve · publish everywhere", icon: "send" },
        ],
      },
    ],
  },

  {
    slug: "seasonal-calendar",
    stage: 2,
    category: "marketing",
    name: "Seasonal Marketing Calendar",
    iconName: "sun",
    tagline: "A month of moves — planned in five minutes.",
    elevator: "Look 7, 30 or 365 days ahead. The assistant lays out what to promote, when, matched to your harvests, your markets, and past-campaign performance.",
    what: [
      "Weekly view: what to post, when, what to email — laid out by day.",
      "Monthly view: aligned to your harvests and market calendar.",
      "Annual view: a running plan you edit once a season.",
      "Every plan-item stays a draft until you approve it.",
    ],
    how: [
      { title: "Sync your farm calendar", body: "Harvests, market days, delivery days, events." },
      { title: "Pick a horizon", body: "This week · this month · this year." },
      { title: "Approve items as they come up", body: "The plan reminds you the day before each campaign." },
    ],
    status: "designed",
    deepLink: null,
    mocks: [
      {
        eyebrow: "This week",
        title: "Four planned moves",
        subtitle: "Every card fires the day before — you approve before it goes out.",
        blocks: [
          { kind: "list", items: [
            { icon: "send",     title: "Wed · Roma peak post",         sub: "Social + farm page banner",   right: "Draft" },
            { icon: "envelope", title: "Thu · Weekly newsletter",       sub: "218 past customers",         right: "Draft" },
            { icon: "shop",     title: "Sat · Market-day reminder",     sub: "WhatsApp · 46 regulars",     right: "Draft" },
            { icon: "handshake",title: "Sun · Wholesale sheet refresh", sub: "3 restaurant contacts",       right: "Draft" },
          ]},
        ],
      },
      {
        eyebrow: "Month ahead",
        title: "September at Green Valley",
        subtitle: "Aligned to harvests, markets, and last year's best-performing weeks.",
        blocks: [
          { kind: "kv-grid", items: [
            { label: "Week 1 focus", value: "Roma peak · school-buyer pitch" },
            { label: "Week 2 focus", value: "CSA renewal reminder" },
            { label: "Week 3 focus", value: "Farm-stand extended hours" },
            { label: "Week 4 focus", value: "Fall CSA enrollment opens" },
          ]},
          { kind: "info", text: "Two suggestions from last year's data are marked · tap to accept or skip.", tone: "ok" },
        ],
      },
    ],
  },

  {
    slug: "local-seo",
    stage: 2,
    category: "marketing",
    name: "Local Farm Discovery & SEO",
    iconName: "magnify",
    tagline: "Get found by neighbors who don't know you yet.",
    elevator: "Local search page copy, a Google Business Profile update, and three plain-language FAQs. Keyword-natural, never stuffed, always in your voice.",
    what: [
      "Farm page rewrites tuned to your town + what you grow.",
      "Google Business Profile update draft — hours, categories, photos, description.",
      "Three FAQ prompts your neighbors actually search for.",
      "'Where to buy [crop] near [town]' pages that stay factual.",
    ],
    how: [
      { title: "Confirm the basics", body: "Farm name, town, what you sell, when you're open." },
      { title: "Approve the drafts", body: "Home page, Google Business Profile update, and three FAQs." },
      { title: "Watch the traffic climb", body: "The dashboard shows real search terms bringing you visitors." },
    ],
    status: "designed",
    deepLink: null,
    mocks: [
      {
        eyebrow: "Local discovery",
        title: "Confirm the basics",
        blocks: [
          { kind: "input", label: "Farm town",  value: "River City, Oregon", icon: "map" },
          { kind: "chips", label: "What we sell", options: ["Roma", "Curry leaves", "CSA boxes", "Farm stand"], active: "Roma" },
          { kind: "chips", label: "Open days",   options: ["Sat", "Sun", "By appointment"], active: "Sat" },
          { kind: "cta", label: "Draft the discovery pack", icon: "magnify" },
        ],
      },
      {
        eyebrow: "Discovery pack",
        title: "Three drafts ready",
        badge: "Approval required",
        blocks: [
          { kind: "textarea", label: "Farm page opener",
            value: "Green Valley Farm — no-spray Roma tomatoes and curry leaves, from a family farm 12 minutes south of River City." },
          { kind: "textarea", label: "Google Business Profile update",
            value: "Open Saturdays 8 AM–6 PM · farm-stand pickup, CSA delivery in River City. Reserve a flat by Friday." },
          { kind: "list", title: "FAQs neighbors search for", items: [
            { icon: "check", title: "Where can I buy fresh tomatoes near River City?" },
            { icon: "check", title: "Do you have a farm stand open Saturday?" },
            { icon: "check", title: "How does the CSA work?" },
          ]},
          { kind: "cta", label: "Approve · publish drafts", icon: "send" },
        ],
      },
    ],
  },

  {
    slug: "reviews-loyalty",
    stage: 2,
    category: "marketing",
    name: "Reviews, Referrals & Loyalty",
    iconName: "sparkle",
    tagline: "Turn one sale into three.",
    elevator: "Post-purchase review requests, a referral offer for regulars, and a loyalty note for people who've bought three times or more. Every message goes out only after you approve.",
    what: [
      "Auto-drafted review request the day after a customer picks up.",
      "Referral offer with your own words — the assistant matches your tone.",
      "Loyalty campaigns triggered on 3 · 5 · 10 orders — approve each one.",
      "Never sends without your tap.",
    ],
    how: [
      { title: "Pick who to talk to", body: "Recent buyers · repeat regulars · dormant customers." },
      { title: "Pick the moment", body: "Right after pickup · one week later · one month later." },
      { title: "Approve every send", body: "You see the exact words before anything sends." },
    ],
    status: "designed",
    deepLink: null,
    mocks: [
      {
        eyebrow: "Post-purchase",
        title: "3 customers ready for a review ask",
        subtitle: "Picked up in the last 72 hours. You approve every send.",
        blocks: [
          { kind: "list", items: [
            { icon: "check", title: "Chef Miguel · Basil Bistro", sub: "Roma flat · 4 PM Fri", right: "Draft" },
            { icon: "check", title: "River City Co-op",           sub: "6 crates · Sat delivery", right: "Draft" },
            { icon: "check", title: "The Fenton family",          sub: "Farm-stand bag · Sat",   right: "Draft" },
          ]},
          { kind: "textarea", label: "Draft note",
            value: "Hi Miguel — hope those Romas landed well at the Bistro. If they did, a quick line on our page helps the farm hugely. If not, tell me — I'd rather know." },
        ],
      },
      {
        eyebrow: "Loyalty triggers",
        title: "Regulars who've hit a milestone",
        subtitle: "Three thank-you drafts, ready to approve.",
        blocks: [
          { kind: "list", items: [
            { icon: "sparkle", title: "Reeta S. · 5th order",  sub: "Free bunch of curry leaves next visit", right: "Draft" },
            { icon: "sparkle", title: "The Fenton family · 10th",   sub: "Farm-stand-only free tomato flat", right: "Draft" },
            { icon: "sparkle", title: "River Co-op · 3rd wholesale", sub: "Referral offer to their staff",   right: "Draft" },
          ]},
          { kind: "info", text: "Nothing sends until you approve each one.", tone: "ok" },
        ],
      },
    ],
  },

  {
    slug: "csa-farm-box",
    stage: 3,
    category: "marketing",
    name: "CSA & Farm Box Marketing",
    iconName: "package",
    tagline: "Fill every box, every week.",
    elevator: "Enrollment landing page, weekly box preview post, waitlist notes and a renewal reminder — all written for you, all in your farm's voice.",
    what: [
      "Enrollment landing copy tuned to your CSA season and price.",
      "Weekly 'what's in the box' post drafted from your pack list.",
      "Waitlist and renewal reminders on autopilot — approval-first.",
      "Referral offers for members who bring a friend.",
    ],
    how: [
      { title: "Set the season", body: "Start · length · box size · price." },
      { title: "Approve the enrollment page", body: "Hero, promise, FAQ, sign-up form." },
      { title: "Approve each weekly send", body: "Pack list turns into a preview post every Sunday." },
    ],
    status: "designed",
    deepLink: null,
    mocks: [
      {
        eyebrow: "CSA enrollment",
        title: "Fall 2026 · 10-week share",
        subtitle: "Approve the enrollment page and the assistant handles the weekly comms.",
        blocks: [
          { kind: "stat-row", items: [
            { label: "Share price", value: "$285" },
            { label: "Weeks",       value: "10" },
          ]},
          { kind: "textarea", label: "Landing hero",
            value: "Ten weeks of no-spray produce from Green Valley — picked Friday, in your box by Saturday morning. Only 40 shares." },
          { kind: "chips", label: "Add-ons offered", options: ["Egg share", "Bread share", "Bouquet"], active: "Egg share" },
          { kind: "cta", label: "Approve · open enrollment", icon: "send" },
        ],
      },
      {
        eyebrow: "Week 3 preview",
        title: "This week's box",
        subtitle: "Draft goes out every Sunday morning — you approve.",
        blocks: [
          { kind: "list", items: [
            { icon: "basket", title: "Roma tomatoes",  sub: "2 kg — sweetest of the season" },
            { icon: "leaf",   title: "Curry leaves",   sub: "1 bunch — back after the rain break" },
            { icon: "basket", title: "Baby bok choy",  sub: "3 heads — great for a stir-fry" },
            { icon: "basket", title: "Bitter gourd",   sub: "1 kg — for anyone brave enough" },
          ]},
          { kind: "info", text: "Copy · 'Warning: the bitter gourd is not for everyone.' You can edit before it sends." },
        ],
      },
    ],
  },

  {
    slug: "market-promoter",
    stage: 3,
    category: "marketing",
    name: "Farmers Market & Farm Stand Promoter",
    iconName: "shop",
    tagline: "Draw the crowd before you drive there.",
    elevator: "One tap turns your market schedule into a full multi-channel push — social, WhatsApp, and a printable QR flyer. All approval-first.",
    what: [
      "Market-day post pulls from your inventory on the morning of.",
      "WhatsApp blast to regulars 90 minutes before you open.",
      "QR flyer copy for the market table.",
      "'Only at market today' offers surface here to drive traffic.",
    ],
    how: [
      { title: "Pick the market", body: "From your calendar — Saturday River City, first Sunday Portland." },
      { title: "Approve the packet", body: "Social + WhatsApp + QR flyer, all reviewed at once." },
      { title: "Head out", body: "The packet sends 90 minutes before you open." },
    ],
    status: "designed",
    deepLink: null,
    mocks: [
      {
        eyebrow: "Market day · Saturday",
        title: "River City Market — 9 to 2",
        subtitle: "Approve the full push · sends 90 min before you open.",
        blocks: [
          { kind: "chips", label: "Bringing to market", options: ["Roma", "Curry leaves", "Baby bok choy", "Bitter gourd"], active: "Roma" },
          { kind: "textarea", label: "Social + farm page",
            value: "River City Market tomorrow, 9–2. Roma peak, curry leaves back, and a market-only strawberry flat while they last." },
          { kind: "textarea", label: "WhatsApp blast",
            value: "See you at the market tomorrow · 9–2. Reserve a strawberry flat — 12 available." },
          { kind: "cta", label: "Approve · queue for 7:30 AM", icon: "send" },
        ],
      },
      {
        eyebrow: "QR flyer copy",
        title: "For the market table",
        subtitle: "Print · fold · prop up next to the samples.",
        blocks: [
          { kind: "textarea", label: "Front",
            value: "Green Valley Farm · No spray · Picked this morning · Ask us anything." },
          { kind: "textarea", label: "Under the QR",
            value: "Scan for what's ready this week — updated live." },
          { kind: "info", text: "The QR opens your live 'Fresh Today' page — nothing static to reprint.", tone: "ok" },
          { kind: "cta", label: "Print · A4 duplex", icon: "sparkle" },
        ],
      },
    ],
  },

  {
    slug: "wholesale-buyer-marketing",
    stage: 3,
    category: "marketing",
    name: "Wholesale & Buyer Marketing",
    iconName: "handshake",
    tagline: "The pitch you don't have to write.",
    elevator: "Your availability becomes a wholesale sheet, a restaurant pitch, and a school-district quote request — professional, brief, ready to send.",
    what: [
      "Availability sheet pulled from your inventory + harvest calendar.",
      "Restaurant pitch tuned to the chef's cuisine and delivery day.",
      "School / institution quote requests with the paperwork prompts.",
      "Follow-up drafts if a buyer goes quiet after a first reply.",
    ],
    how: [
      { title: "Confirm what's available", body: "The assistant pre-fills from inventory — you adjust." },
      { title: "Pick a buyer type", body: "Restaurant · grocer · school · food hub." },
      { title: "Approve · send", body: "Every message shows up in your buyer inbox with the reply thread." },
    ],
    status: "designed",
    deepLink: null,
    mocks: [
      {
        eyebrow: "Availability sheet",
        title: "This week's wholesale sheet",
        subtitle: "Pre-filled from your inventory + harvest calendar.",
        blocks: [
          { kind: "list", items: [
            { icon: "basket", title: "Roma tomato",   sub: "120 kg · $2.10 wholesale · Tue+Fri delivery", right: "Include" },
            { icon: "basket", title: "Baby bok choy", sub: "40 kg · $10.50 wholesale · Fri only",         right: "Include" },
            { icon: "leaf",   title: "Curry leaves",  sub: "Bunches · MOQ 50 · same-day cut",             right: "Skip" },
          ]},
          { kind: "cta", label: "Draft the outreach", icon: "sparkle" },
        ],
      },
      {
        eyebrow: "Restaurant pitch draft",
        title: "For Basil Bistro",
        subtitle: "Chef Miguel · Italian · Fri delivery preferred.",
        badge: "Approval required",
        blocks: [
          { kind: "textarea", label: "Subject", value: "Roma tomatoes at peak · this week's Green Valley sheet" },
          { kind: "textarea", label: "Body",
            value: "Miguel — Romas hit peak flavor this week. 120 kg available at $2.10 wholesale, Friday delivery. Curry leaves back too if you're using them. Full sheet attached — happy to hold you a case." },
          { kind: "info", text: "If no reply by Thursday morning, a soft follow-up drafts automatically.", tone: "ok" },
          { kind: "cta", label: "Approve · send", icon: "send" },
        ],
      },
    ],
  },

  {
    slug: "marketing-coach",
    stage: 3,
    category: "marketing",
    name: "Marketing Coach & Sales Analyzer",
    iconName: "chart",
    tagline: "What worked. What didn't. What next.",
    elevator: "A plain-language weekly readout of what sold, what didn't, who came back — and the one campaign the assistant recommends running this week.",
    what: [
      "Weekly readout in plain terms — no jargon, no dashboards to decode.",
      "'What worked' shows the best-performing post, product and channel.",
      "'What to try next' is a single specific recommendation you can tap to draft.",
      "Referenced by every other marketing tool to keep improving.",
    ],
    how: [
      { title: "Open the coach on Monday", body: "The week's readout is already waiting." },
      { title: "Read the three highlights", body: "Best sale · quiet product · new repeat customer." },
      { title: "Tap the one recommendation", body: "It drafts the full campaign in one pass." },
    ],
    status: "designed",
    deepLink: null,
    mocks: [
      {
        eyebrow: "This week",
        title: "3 highlights and one move",
        subtitle: "Plain-language weekly readout · Monday morning.",
        blocks: [
          { kind: "list", items: [
            { icon: "check",   title: "Your best sale",   sub: "Roma flash offer · sold out in 6 hours" },
            { icon: "warning", title: "Quiet this week",  sub: "Bitter gourd · 12 kg unsold — try a bundle" },
            { icon: "sparkle", title: "New repeat customer", sub: "The Fenton family · 3rd order in 6 weeks" },
          ]},
        ],
      },
      {
        eyebrow: "One move for the week",
        title: "Bundle the bitter gourd with a curry-night box",
        subtitle: "The coach's single specific recommendation.",
        blocks: [
          { kind: "kv-grid", items: [
            { label: "Bundle price",  value: "$14" },
            { label: "Break-even",    value: "$8.20" },
            { label: "Best channel",  value: "WhatsApp · CSA members" },
            { label: "Predicted units", value: "18 of 24 bundles" },
          ]},
          { kind: "info", text: "Tap to draft the full campaign in one pass — social, WhatsApp, farm page.", tone: "ok" },
          { kind: "cta", label: "Draft this campaign", icon: "sparkle" },
        ],
      },
    ],
  },
];
